import { DirectSalesNegotiationTypeConfig } from 'components/common/types/DirectSalesNegotiationTypeConfig';
import { DirectSalesMediaType, DirectSalesPricingMode } from 'components/common/types/DirectSalesCampaign.types';
import { PublisherTolerances } from 'utils/getPublisherDefaultCriteria';
import { Dsp } from './Deal.types';
import { MetricType } from './DrilldownReports.types';

type ExternalId = {
  system: string;
  ids: string[];
};

export type Locale = {
  localeCode: string;
  timezone: string;
  latitude: string;
  currencyCode: string;
  longitude: string;
};

export enum PublisherType {
  PUBLISHER = 'PUBLISHER',
  RESELLER = 'RESELLER',
}

export type PublisherDirectSalesPricing = {
  gridConfiguration?: string;
  startDateOffset?: string;
  reportConfiguration?: string;
  campaignFieldsConfiguration?: Record<string, boolean>;
  frameListConfiguration?: string;
};

export type PublisherReportConfiguration = {
  metricType: MetricType;
};

export type PublisherFrameListConfiguration = {
  metadataFields: string[];
};

export type TransformedPublisherDirectSalesPricing = {
  type: string;
  startDay: string;
  ids: string[];
  pivotFields: string[];
  reportingFields: string[];
  negotiationTypeConfig?: DirectSalesNegotiationTypeConfig;
};

export const PUBLISHER_DEFAULT_PRICING_MODE_DIGITAL_PAPER_KEY = 'DIGITAL_PAPER';

export type PublisherDefaultPricingModeByMediaTypeKey =
  | DirectSalesMediaType
  | typeof PUBLISHER_DEFAULT_PRICING_MODE_DIGITAL_PAPER_KEY;

export type PublisherDefaultPricingModeByMediaType = Partial<
  Record<PublisherDefaultPricingModeByMediaTypeKey, DirectSalesPricingMode.CPM | DirectSalesPricingMode.PANEL>
>;

export type PublisherCampaignConfiguration = {
  defaultPricingModeByMediaType?: PublisherDefaultPricingModeByMediaType;
};

export type PublisherConfiguration = {
  currencyCode: string;
  flag?: string;
  iso2CountryCode: string;
  marketId: string;
  latitude: number;
  localeCode: string;
  longitude: number;
  openStreetMapCountryName: string;
  timezone: string;
  type: PublisherType;
  allocationReportDefaultRowGroups?: string[];
  isAllocationReportSelectAllDefault?: boolean;
  isAllocationReportSelectAllLocked?: boolean;
  isAllocationReportSelectAllHidden?: boolean;
  availabilityReportDefaultRowGroups?: string[];
  paperWeekStart?: number;
  timezoneOverride?: string;
};

export type PublisherConfigurationResponse = {
  appId: string;
  applicationConfiguration: {
    countryName: string;
    localeCode: string;
    latitude: string;
    longitude: string;
    flag: string;
    allocationReportDefaultRowGroups?: string;
    isAllocationReportSelectAllDefault?: string;
    isAllocationReportSelectAllLocked?: string;
    isAllocationReportSelectAllHidden?: string;
    timezoneOverride?: string;
  };
  currencyCode: string;
  id: string;
  identifier: string;
  name: string;
  iso2CountryCode: string;
  iso3CountryCode: string;
  timeZone: string;
  type: PublisherType;
};

export type PublisherFeatures = {
  [featureName: string]: string;
};

export type TransformedPublisherFeatures = {
  [featureName: string]: string[];
};

export type PublisherDspsMediaOwner = { [dspId: string]: Dsp };

export type PublisherStore = {
  configuration: PublisherConfiguration;
  campaignConfiguration: PublisherCampaignConfiguration;
  directSalesPricing: TransformedPublisherDirectSalesPricing;
  reportConfiguration: PublisherReportConfiguration;
  frameListConfiguration: PublisherFrameListConfiguration;
  directSalesRequiredFields?: PublisherCampaignRequiredFieldsConfig;
  dsps: PublisherDspsMediaOwner;
  publisherFeatures: TransformedPublisherFeatures;
  startDateOffset: number;
  targetTolerances?: PublisherTolerances;
};

export type PublisherTestConfiguration = {
  [key: string]: {
    configuration: {
      currencyCode: string;
      localeCode: string;
      marketId: string;
    };
  };
};

export type Publisher = {
  id: string;
  marketId: string | null;
  name: string;
  externalIds: ExternalId[];
  version: number;
  created: string;
  updated: string;
  domain: string | null;
  currency: string | null;
  timeZone: string | null;
  iso2CountryCode: string | null;
  iso3CountryCode: string | null;
  dspTradingName: string | null;
  locale?: Locale;
};

export interface PublisherCampaignConfig extends PublisherTolerances {
  campaignLockExpiryTime: string;
  reportingTokenExpiryDays: string;
  requiredFields?: string;
  defaultPricingModeByMediaType?: string;
}

export interface PublisherVioohCentralConfig {
  v2_asset_template_enabled: string;
}

export type PublisherCampaignRequiredFieldsConfig = { campaign: string[]; line: string[] };
